import * as React from "react";
import {
	Alert,
	AlertTitle,
	Autocomplete,
	Box,
	Button,
	Card,
	CardContent,
	CardHeader,
	Chip,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { ArrowClockwiseIcon, LockKeyIcon, PlusIcon } from "@phosphor-icons/react";

import { apiErrorMessage, statusCode } from "@/lib/admin-formatters";
import type { Account } from "@/hooks/api/use-accounts";
import {
	useDeletePriceBookItem,
	usePriceBook,
	useUpsertPriceBookItem,
	type PriceBookItem,
	type PriceBookPayload,
} from "@/hooks/api/use-campaign-requests";
import { useDialog } from "@/hooks/use-dialog";
import { useAuth } from "@/components/core/auth-provider";
import { DeleteModal } from "@/components/deleteModal/delete-modal";
import { TableAnimation } from "@/components/table-animation/table-animation";

import { marketLabel, PRICE_CODE_LABELS } from "../constants";
import { labelOf } from "../humanise";
import { BlockedRates } from "./blocked-rates";
import { ConfirmRateDialog } from "./confirm-rate-dialog";
import { RateFormDrawer, type RateDrawerTarget } from "./rate-form-drawer";
import { buildCarrierRows, buildChargeRows, summariseRates, type RateRow } from "./rate-rows";
import { RatesTable } from "./rates-table";
import { usePriceBookAccounts } from "./use-price-book-accounts";

/** A rate and the row it came from, held together so the delete copy can name what it removes. */
interface DeleteTarget {
	row: RateRow;
	item: PriceBookItem;
}

/**
 * The price book: what we charge, who is charged differently, and what is not agreed yet.
 *
 * Super-admin only, and the API says so too — `price_book_item` is a public-schema table and one
 * row here governs what every workspace is quoted. A 403 is a legitimate answer to this screen
 * rather than a bug, so it is explained rather than swallowed.
 *
 * The screen has one job beyond editing: making an unconfirmed rate impossible to miss. Ten of the
 * eleven seeded rates are working numbers nobody has agreed, and each of them ends one of two ways
 * — the pricer refuses to quote against it and the client meets a 422 they cannot act on, or the
 * pricer does not recognise the wording of its note and quotes a client a price nobody set. Amber
 * here is the only place either failure is visible to someone who can fix it.
 *
 * What made that visible is `unconfirmed` rather than `placeholder`: the badge, the row wash and
 * the counts all read the note as a person would, and only the copy that claims a rate *blocks*
 * quoting is allowed to depend on the marker the server actually matches on.
 */
export function PriceBook(): React.JSX.Element {
	const { user } = useAuth();
	const isSuperAdmin = Boolean(user?.isSuperAdmin);

	const [account, setAccount] = React.useState<Account | null>(null);
	const accountId = account?.id ?? null;
	const accountName = account?.name ?? null;

	const [editingKey, setEditingKey] = React.useState<string | null>(null);
	const [drawerTarget, setDrawerTarget] = React.useState<RateDrawerTarget | null>(null);
	const [confirming, setConfirming] = React.useState<PriceBookItem | null>(null);

	const { data: items, isLoading, isFetching, isError, error, refetch } = usePriceBook(accountId, isSuperAdmin);

	const accountsQuery = usePriceBookAccounts(isSuperAdmin);

	const upsertMutation = useUpsertPriceBookItem();
	const deleteMutation = useDeletePriceBookItem();

	const {
		data: deleteTarget,
		open: isDeleteOpen,
		handleOpen: openDelete,
		handleClose: closeDelete,
	} = useDialog<DeleteTarget | null>();

	const rates = React.useMemo(() => items ?? [], [items]);
	const chargeRows = React.useMemo(() => buildChargeRows(rates, accountId), [rates, accountId]);
	const carrierRows = React.useMemo(() => buildCarrierRows(rates, accountId), [rates, accountId]);
	const summary = React.useMemo(() => summariseRates([...chargeRows, ...carrierRows]), [chargeRows, carrierRows]);

	const forbidden = !isSuperAdmin || statusCode(error) === 403;
	const isSaving = upsertMutation.isPending || deleteMutation.isPending;

	/** Whose rate an item is, in words. The id itself is never rendered. */
	const scopeLabelFor = React.useCallback(
		(item: PriceBookItem | null): string => {
			if (!item?.accountId) return "the platform default — every account without an override";
			return item.accountId === accountId ? (accountName ?? "this account") : "this account";
		},
		[accountId, accountName]
	);

	const handleSave = async (payload: PriceBookPayload) => {
		try {
			await upsertMutation.mutateAsync(payload);
			setEditingKey(null);
			setDrawerTarget(null);
			setConfirming(null);
		} catch {
			// The mutation toasts the API's own message — a rejected amount or a currency the
			// account is not priced in has to reach the operator verbatim. Keep the form open.
		}
	};

	const handleDelete = async () => {
		if (!deleteTarget) return;

		try {
			await deleteMutation.mutateAsync(deleteTarget.item.id);
		} finally {
			closeDelete();
		}
	};

	if (forbidden) {
		return (
			<Box
				sx={{
					maxWidth: "var(--Content-maxWidth)",
					m: "var(--Content-margin)",
					p: "var(--Content-padding)",
					width: "var(--Content-width)",
				}}
			>
				<Stack spacing={3}>
					<Box>
						<Typography variant="h4">Price Book</Typography>
					</Box>
					<Alert severity="warning" icon={<LockKeyIcon />}>
						<AlertTitle>This screen is for platform administrators</AlertTitle>
						<Typography variant="body2">
							Your own rates are on every quote you are sent, line by line. To change one, ask your account manager.
						</Typography>
					</Alert>
				</Stack>
			</Box>
		);
	}

	return (
		<React.Fragment>
			<Box
				sx={{
					maxWidth: "var(--Content-maxWidth)",
					m: "var(--Content-margin)",
					p: "var(--Content-padding)",
					width: "var(--Content-width)",
				}}
			>
				<Stack spacing={4}>
					<Stack
						direction={{ xs: "column", sm: "row" }}
						spacing={3}
						sx={{ alignItems: { xs: "flex-start", sm: "center" } }}
					>
						<Box sx={{ flex: "1 1 auto" }}>
							<Typography variant="h4">Price Book</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
								{accountName
									? `The rates ${accountName} is quoted — its own overrides, and the platform defaults it falls back to`
									: "The platform default rates every account is quoted from"}
							</Typography>
						</Box>
						<Stack direction="row" spacing={1}>
							<Button
								startIcon={<ArrowClockwiseIcon />}
								variant="outlined"
								onClick={() => refetch()}
								disabled={isFetching}
							>
								Refresh
							</Button>
							<Button
								startIcon={<PlusIcon />}
								variant="contained"
								onClick={() =>
									setDrawerTarget({
										accountId,
										scopeLabel: accountName ?? "the platform",
									})
								}
							>
								{accountName ? "New override" : "New rate"}
							</Button>
						</Stack>
					</Stack>

					<Card>
						<CardContent>
							<Stack
								direction={{ xs: "column", md: "row" }}
								spacing={2}
								sx={{ alignItems: { xs: "stretch", md: "center" } }}
							>
								<Autocomplete<Account>
									sx={{ minWidth: 320 }}
									options={accountsQuery.data?.accounts ?? []}
									getOptionLabel={(option) => option.name}
									isOptionEqualToValue={(option, value) => option.id === value.id}
									value={account}
									onChange={(_event, value) => {
										setAccount(value);
										setEditingKey(null);
									}}
									loading={accountsQuery.isLoading}
									disabled={accountsQuery.isError}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Show rates for"
											placeholder="Platform defaults"
											helperText={
												accountsQuery.isError
													? "Could not load the account list — the platform defaults are still editable."
													: accountsQuery.data?.truncated
														? `Showing the first ${accountsQuery.data.accounts.length} of ${accountsQuery.data.total} accounts.`
														: "Clear this to go back to the platform defaults."
											}
										/>
									)}
								/>

								<Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
									{accountName ? (
										<Chip
											color="primary"
											variant="soft"
											label={`${summary.overrides} override${summary.overrides === 1 ? "" : "s"} for ${accountName}`}
										/>
									) : (
										<Chip variant="outlined" label="Platform defaults" />
									)}
									{summary.unconfirmed > 0 ? (
										<Chip color="warning" variant="soft" label={`${summary.unconfirmed} unconfirmed`} />
									) : null}
									{summary.missing > 0 ? (
										<Chip color="warning" variant="outlined" label={`${summary.missing} not set`} />
									) : null}
								</Stack>
							</Stack>

							{/* The "Reading these tables" glossary that stood here spent three paragraphs
							    defining three badges that read "Confirmed", "Unconfirmed" and "Not set".
							    The counts are already on the chips above; the amber row wash and the
							    Confirm button say the rest. */}
						</CardContent>
					</Card>

					{isError && statusCode(error) !== 403 ? (
						<Alert
							severity="error"
							action={
								<Button color="inherit" size="small" onClick={() => refetch()}>
									Retry
								</Button>
							}
						>
							<AlertTitle>Could not load the price book</AlertTitle>
							<Typography variant="body2">{apiErrorMessage(error, "Something went wrong.")}</Typography>
						</Alert>
					) : null}

					{/* Named rather than counted. "10 rates are not confirmed" sends an operator
					    hunting through two tables; the chips send them to the row. */}
					<BlockedRates rows={[...chargeRows, ...carrierRows]} context="price-book" />

					<TableAnimation delay={0}>
						<Card>
							<CardHeader
								title="Charges"
								subheader={
									accountName
										? `What ${accountName} is charged. An override is coloured; anything else is inherited from the platform.`
										: "What every account is charged unless it has negotiated its own rate"
								}
							/>
							<RatesTable
								rows={chargeRows}
								accountId={accountId}
								accountName={accountName}
								isLoading={isLoading}
								emptyText="No charges are set. Nothing can be quoted until they are."
								editingKey={editingKey}
								isSaving={isSaving}
								onEdit={(row) => setEditingKey(row.key)}
								onCancelEdit={() => setEditingKey(null)}
								onSaveEdit={handleSave}
								onSet={(row) =>
									setDrawerTarget({
										accountId,
										scopeLabel: accountName ?? "the platform",
										code: row.code,
										market: row.market,
										inheritedFrom: row.effective,
										locked: true,
									})
								}
								onConfirm={setConfirming}
								onDelete={(row, item) => openDelete({ row, item })}
							/>
						</Card>
					</TableAnimation>

					<TableAnimation delay={0}>
						<Card>
							<CardHeader
								title="Carrier rates"
								subheader="Telco termination, passed through at cost. Priced per market."
							/>
							<RatesTable
								rows={carrierRows}
								accountId={accountId}
								accountName={accountName}
								isLoading={isLoading}
								emptyText="No carrier rates are set."
								editingKey={editingKey}
								isSaving={isSaving}
								onEdit={(row) => setEditingKey(row.key)}
								onCancelEdit={() => setEditingKey(null)}
								onSaveEdit={handleSave}
								onSet={(row) =>
									setDrawerTarget({
										accountId,
										scopeLabel: accountName ?? "the platform",
										code: row.code,
										market: row.market,
										inheritedFrom: row.effective,
										locked: true,
									})
								}
								onConfirm={setConfirming}
								onDelete={(row, item) => openDelete({ row, item })}
							/>
						</Card>
					</TableAnimation>

					{/* A paragraph explaining that quotes snapshot their rates. The delete dialog
					    already says it at the only moment it can cost anybody anything. */}
				</Stack>
			</Box>

			<RateFormDrawer
				open={Boolean(drawerTarget)}
				target={drawerTarget}
				isSaving={upsertMutation.isPending}
				onClose={() => setDrawerTarget(null)}
				onSave={handleSave}
			/>

			<ConfirmRateDialog
				open={Boolean(confirming)}
				item={confirming}
				scopeLabel={scopeLabelFor(confirming)}
				isSaving={upsertMutation.isPending}
				onClose={() => setConfirming(null)}
				onConfirm={handleSave}
			/>

			<DeleteModal
				open={isDeleteOpen}
				onClose={closeDelete}
				onDelete={handleDelete}
				// The account's own name goes in the title, which renders as text. `description` is put
				// through `dangerouslySetInnerHTML`, and an account name is a string its own admins
				// choose — it must not reach a super admin's session as markup.
				title={
					deleteTarget?.item.accountId
						? `Remove ${accountName ?? "this account"}'s override`
						: "Remove this platform rate"
				}
				description={
					deleteTarget
						? `${labelOf(PRICE_CODE_LABELS, deleteTarget.item.code)}${
								deleteTarget.item.market ? ` · ${marketLabel(deleteTarget.item.market)}` : ""
							} — ${
								deleteTarget.item.accountId
									? "this account falls back to the platform default."
									: "every account loses this rate."
							}`
						: ""
				}
				warningText={
					deleteTarget?.item.accountId
						? "Quotes already confirmed keep their own frozen rates and are unaffected."
						: "With nothing set, no new request that needs this charge can be quoted until it is set again. Quotes already confirmed are unaffected."
				}
				deleteText="Remove rate"
			/>
		</React.Fragment>
	);
}
